import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {FontButtonItem} from '@Components/font-selector-panel/components/font-button-item';
import type {OnSelectFontFamilyCallback} from '@Components/font-selector-panel/font-selector.types';
import {FontsList} from '@Components/font-selector-panel/components/fonts-list';
import type {FontCategory, FontData} from '@Libraries/font-library';
import Fuse from 'fuse.js';
import {getReadBucket} from '@Utils/s3.util';
import {InputField} from '@Components/input-field-v2';
import {InputControlItemType, InputFieldType} from '@Components/input-field-v2/input-field.types';
import {useAppSelector} from '@/hooks';
import styles from './font-search-section.module.scss';
import {isSpecialSeasonFontCategory} from '@Components/font-selector-panel/components/try-seasonal-font-button/try-seasonal-font-button.types';

interface FontSearchSectionProps {
  className?: string;

  /**
   * If this is sent, then the behavior becomes dumb for selection
   */
  selectedFontFamily?: string;
  isSelectedFontLoading?: boolean;
  onSelectFontFamily?: OnSelectFontFamilyCallback;
  hasSmallTopPadding?: boolean;

  selectedCategory?: FontCategory;
  autoFocusSearchInputField?: boolean;
}

export function FontSearchSection({
  hasSmallTopPadding = false,
  className = '',
  selectedFontFamily = '',
  isSelectedFontLoading = false,
  autoFocusSearchInputField = true,
  onSelectFontFamily = (): void => {},
  ...props
}: FontSearchSectionProps): ReactElement<any> | null {
  const [fontCategories, setFontCategories] = useState<FontCategory[]>();
  const allFonts = useAppSelector((state) => {
    return state.fontSelector.pmwFonts as FontData[] | null;
  });
  const userFonts = useAppSelector((state) => {
    return state.fontSelector.userFonts as FontData[] | null;
  });
  const [clickedCategory, setClickedCategory] = useState(props.selectedCategory);
  const [searchTerm, setSearchTerm] = useState('');
  const inputFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void getFontCategories();

    if (inputFieldRef?.current && autoFocusSearchInputField) {
      inputFieldRef.current.focus();
    }
  }, []);

  const getFontCategories = async (): Promise<void> => {
    try {
      const response = await window.PMW.readLocal('font/getFontCategories');
      setFontCategories(response);
    } catch {
      window.PMW.openErrorModal({
        message: window.i18next.t('pmwjs_general_ajax_error'),
      });
    }
  };

  const getImageUrlFromName = (id: number): string => {
    return window.PMW.util.repoURL(`fonts/${id}.png?ts=${new Date().getTime()}`, getReadBucket()) as string;
  };

  const onClickCategory = (category: FontCategory): void => {
    setClickedCategory(category);
  };

  const doShowFilterOverlayWhenSelected = (category: FontCategory): boolean => {
    return !isSpecialSeasonFontCategory(category);
  };

  const getClassnameForFontCategoryImageThumb = (category: FontCategory): string | undefined => {
    if (isSpecialSeasonFontCategory(category)) {
      return 'spacing-p-l-2 spacing-p-r-2';
    }

    return undefined;
  };

  const prepFontCategories = (): ReactElement<any>[] | null => {
    if (fontCategories) {
      const categories: ReactElement<any>[] = [];
      fontCategories.map((category) => {
        categories.push(
          <FontButtonItem
            key={category.idfontcategory}
            isSelected={category.idfontcategory === clickedCategory?.idfontcategory}
            onClick={() => {
              onClickCategory(category);
            }}
            imageUrl={getImageUrlFromName(category.idfontcategory)}
            id={category.idfontcategory.toString()}
            showFilterOverlayWhenSelected={doShowFilterOverlayWhenSelected(category)}
            className={getClassnameForFontCategoryImageThumb(category)}
          />
        );
        return categories;
      });

      return categories;
    }
    return null;
  };

  const checkCategory = (category: FontData): boolean => {
    return !!(clickedCategory && category.category.includes(clickedCategory.name));
  };

  const getAllCategoryItems = (): FontData[] | undefined => {
    return allFonts?.filter(checkCategory);
  };

  const getSimilarItems = (): FontData[] => {
    if (!allFonts) {
      return [];
    }
    let allFontsCombined = allFonts;
    if (userFonts) {
      allFontsCombined = [...userFonts, ...allFontsCombined];
    }

    const options = {
      includeScore: true,
      includeMatches: true,
      threshold: 0.2,
      keys: ['name'],
    };

    const fuse = new Fuse(allFontsCombined, options);
    const results = fuse.search(searchTerm);
    return results.map((result) => {
      return result.item;
    });
  };

  const prepSearchResults = (): ReactElement<any> => {
    let results: FontData[] | undefined = [];
    if (clickedCategory) {
      results = getAllCategoryItems();
    }
    if (searchTerm) {
      results = getSimilarItems();
    }
    return <FontsList selectedFontFamily={selectedFontFamily} onSelectFontFamily={onSelectFontFamily} isSelectedFontLoading={isSelectedFontLoading} fontNames={results!} />;
  };

  const prepSearchResultsContainer = (): ReactElement<any> | null => {
    if (!clickedCategory && !searchTerm) {
      return null;
    }
    return <div className="spacing-m-t-3">{prepSearchResults()}</div>;
  };

  return (
    <div className={`flex-v-row ${hasSmallTopPadding ? 'spacingm-m-1' : 'spacing-m-t-4'} ${className}`}>
      <InputField
        ref={inputFieldRef}
        inputClassName={styles.inputFieldFontFamily}
        input={searchTerm}
        onInputChange={(value: string): void => {
          setSearchTerm(value);
        }}
        onInputFocus={() => {
          setClickedCategory(undefined);
        }}
        inputType={InputFieldType.SEARCH}
        placeholder={window.i18next.t('pmwjs_start_search')}
        controls={{
          showDefaultControls: false,
          showSingleDefaultControlOnly: false,
          customControls: [
            {
              type: InputControlItemType.ICONS_ONLY,
              id: 'search-icon',
              icon: searchTerm !== '' ? 'icon-close' : 'icon-search',
            },
          ],
          onClick() {
            if (searchTerm !== '') {
              setSearchTerm('');
            }
          },
        }}
      />
      {!searchTerm ? <ul className={`${styles.section} spacing-m-l-0 spacing-m-r-0 spacing-m-b-3 spacing-m-t-3`}>{prepFontCategories()}</ul> : null}
      {prepSearchResultsContainer()}
    </div>
  );
}
